import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

// Deliberately NOT the shared admin `client` from ../api: that instance carries
// the admin's Authorization header (same-origin localStorage token) and a global
// 401 interceptor that logs out and navigates to the admin login — which would
// render the login page INSIDE this public widget iframe whenever a request
// 401s (e.g. an invalid/placeholder widget key). The widget is anonymous by
// design; it must never share the admin session machinery.
const publicClient = axios.create({ timeout: 60000 });

const createVisitorId = () => {
  if (window?.crypto?.randomUUID) return window.crypto.randomUUID();
  return `pv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/** Reject root-only "/" so building absolute request URLs never yields a host-less string. */
const normalizeApiUrl = (value) => {
  const s = String(value ?? '').trim();
  if (!s || s === '/') return '';
  return s.replace(/\/+$/, '');
};

/*
 * UX modeled on the reference product-page assistant (GuitarCenter PDP):
 *  - Initial state is chips only: suggested questions + an "Ask anything ✨"
 *    pill. No input box, no chat chrome, no name/email gate.
 *  - Tapping a question renders it as a right-aligned pill with the answer as
 *    plain text below, re-offers the remaining questions, and only then shows
 *    the "Ask anything" input with a send arrow.
 *  - Tapping "Ask anything" opens the input directly.
 */

const Panel = styled.div.attrs({ className: 'pa-panel' })`
  font-family: "Poppins", sans-serif;
  padding: 8px;
  box-sizing: border-box;
  color: #111827;
`;

const ChipRow = styled.div.attrs({ className: 'pa-chip-row' })`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const PanelHeading = styled.div.attrs({ className: 'pa-heading' })`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 10px;
`;

const QuestionChip = styled.button.attrs({ className: 'pa-chip' })`
  border: none;
  cursor: pointer;
  border-radius: 100px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  background: var(--pc-chip-bg);
  color: var(--pc-chip-color);
  transition: background 0.15s ease, filter 0.15s ease;
  text-align: left;
  &:hover { filter: brightness(0.95); }
  &:disabled { opacity: 0.55; cursor: default; }
`;

const AskAnythingChip = styled(QuestionChip).attrs({ className: 'pa-chip pa-ask-chip' })`
  background: #eef0f2;
  color: #5f6672;
  display: inline-flex;
  align-items: center;
  gap: 7px;
`;

const Sparkle = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M8 1.5L9.5 6L14 7.5L9.5 9L8 13.5L6.5 9L2 7.5L6.5 6L8 1.5Z" fill="currentColor" />
    <path d="M12.5 1.5L13.25 3.75L15.5 4.5L13.25 5.25L12.5 7.5L11.75 5.25L9.5 4.5L11.75 3.75L12.5 1.5Z" fill="currentColor" opacity="0.8" />
  </svg>
);

const ConversationCard = styled.div.attrs({ className: 'pa-conversation' })`
  background: #f7f8f9;
  border-radius: 14px;
  padding: 18px;
  margin-bottom: 14px;
  /* Long conversations scroll inside the card instead of growing the page. */
  max-height: 380px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: #c9d1d4 transparent;
`;

const Turn = styled.div.attrs({ className: 'pa-turn' })`
  &:not(:first-child) { margin-top: 22px; }
`;

const QuestionPillRow = styled.div.attrs({ className: 'pa-question-row' })`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 14px;
`;

const QuestionPill = styled.div.attrs({ className: 'pa-question' })`
  background: var(--pc-chip-bg);
  color: var(--pc-chip-color);
  border-radius: 100px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  max-width: 85%;
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
`;

const AnswerText = styled.div.attrs({ className: 'pa-answer' })`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.55;
  color: #1f2937;
  white-space: pre-wrap;
  a {
    color: var(--pc-accent);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const RetryButton = styled.button.attrs({ className: 'pa-retry', type: 'button' })`
  margin-top: 6px;
  padding: 4px 10px;
  border: 1px solid currentColor;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  opacity: 0.85;
  &:hover { opacity: 1; }
`;

const Thinking = styled(AnswerText).attrs({ className: 'pa-answer pa-thinking' })`
  color: #6b7280;
  font-weight: 500;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

/**
 * Markdown-lite renderer for answer text. The deterministic fast path (and
 * occasionally the LLM) emits `**bold**` and `[label](url)` — render those as
 * real formatting instead of literal asterisks/brackets. Built as React
 * elements (never innerHTML) so answer content can't inject markup.
 */
const renderAnswerText = (text) => {
  const source = String(text ?? '');
  const nodes = [];
  // One pass over links and bold spans, in order of appearance.
  const pattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*/g;
  let cursor = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(source)) !== null) {
    if (match.index > cursor) nodes.push(source.slice(cursor, match.index));
    if (match[2]) {
      nodes.push(
        <a key={key++} href={match[2]} target="_blank" rel="noopener noreferrer">
          {match[1]}
        </a>,
      );
    } else {
      nodes.push(<strong key={key++}>{match[3]}</strong>);
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < source.length) nodes.push(source.slice(cursor));
  return nodes;
};

const SuggestionsInCard = styled(ChipRow).attrs({ className: 'pa-chip-row pa-suggestions' })`
  margin-top: 22px;
`;

const InputRow = styled.form.attrs({ className: 'pa-input-row' })`
  display: flex;
  align-items: center;
  border: 1px solid #d7dbe0;
  border-radius: 10px;
  background: #fff;
  padding: 4px 6px 4px 16px;
  transition: border-color 0.15s ease;
  &:focus-within { border-color: var(--pc-accent); }
`;

const InputField = styled.input.attrs({ className: 'pa-input' })`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-size: 15px;
  font-family: inherit;
  color: #111827;
  background: transparent;
  padding: 10px 0;
  &::placeholder { color: #9aa1ab; }
`;

const SendButton = styled.button.attrs({ className: 'pa-send' })`
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: transparent;
  color: #9aa1ab;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease, background 0.15s ease;
  &:not(:disabled) { color: var(--pc-accent); }
  &:not(:disabled):hover { background: var(--pc-chip-bg); }
  &:disabled { cursor: default; }
`;

const SendArrow = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PoweredBy = styled.div.attrs({ className: 'pa-powered-by' })`
  font-size: 11px;
  color: #b6bcc4;
  margin-top: 8px;
  text-align: right;
`;

const ProductChatWidget = () => {
  const [ready, setReady] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [widgetKey, setWidgetKey] = useState(null);
  const [productRef, setProductRef] = useState({ entityId: null, sku: null, url: null });
  // Theme code may hand us numbers (unquoted Liquid `{{ product.id }}`); the API wants strings.
  const refString = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const text = String(value).trim();
    return text || null;
  };
  const [resolved, setResolved] = useState(null); // null = not yet known
  const [quickQuestions, setQuickQuestions] = useState([]);
  const [askedQuestions, setAskedQuestions] = useState([]);
  const [primaryColor, setPrimaryColor] = useState('');
  const [headerTitle, setHeaderTitle] = useState('');
  const [customCss, setCustomCss] = useState('');
  const [poweredByText, setPoweredByText] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [visitorId] = useState(() => {
    const existing = localStorage.getItem('chatbot_product_visitor_id');
    if (existing) return existing;
    const id = createVisitorId();
    localStorage.setItem('chatbot_product_visitor_id', id);
    return id;
  });
  const [turns, setTurns] = useState([]); // [{ question, answer, isError }]
  // Chat window stays closed until the visitor taps a question or "Ask anything".
  const [chatOpen, setChatOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const conversationRef = useRef(null);
  const initializedRef = useRef(false);

  // Keep the newest turn in view as the conversation grows past the card's max height.
  useEffect(() => {
    const el = conversationRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  // Handshake with product-widget.js (the parent frame's loader script).
  useEffect(() => {
    function handleMessage(event) {
      const data = event.data;
      if (!data || typeof data !== 'object' || data.action !== 'init-product-chat') return;
      if (initializedRef.current) return;
      initializedRef.current = true;
      setApiUrl(normalizeApiUrl(data.apiUrl));
      setWidgetKey(data.tenantPublicKey || null);
      setProductRef({ entityId: refString(data.entityId), sku: refString(data.sku), url: refString(data.url) });
      setReady(true);
    }
    window.addEventListener('message', handleMessage);
    // Direct (non-iframe) usage for local testing/preview.
    if (!initializedRef.current && window.CHATBOT_PRODUCT_CONFIG) {
      const cfg = window.CHATBOT_PRODUCT_CONFIG;
      initializedRef.current = true;
      setApiUrl(normalizeApiUrl(cfg.apiUrl));
      setWidgetKey(cfg.tenantPublicKey || null);
      setProductRef({
        entityId: refString(cfg.entityId || cfg.productId),
        sku: cfg.sku || null,
        url: cfg.productUrl || null,
      });
      setReady(true);
    }
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Session id persisted per tenant+product so a page refresh keeps the conversation pin.
  const sessionStorageKey = useMemo(() => {
    if (!widgetKey) return null;
    const key = productRef.entityId || productRef.sku || productRef.url || '';
    return `chatbot_product_session_${widgetKey}_${key}`;
  }, [widgetKey, productRef]);

  useEffect(() => {
    if (!sessionStorageKey) return;
    const saved = localStorage.getItem(sessionStorageKey);
    if (saved) setSessionId(saved);
  }, [sessionStorageKey]);

  // Bootstrap: branding + resolved product + suggested-question chips.
  useEffect(() => {
    if (!ready || !apiUrl || !widgetKey) return;
    (async () => {
      try {
        const params = {};
        if (productRef.entityId) params.entity_id = productRef.entityId;
        if (productRef.sku) params.sku = productRef.sku;
        if (productRef.url) params.url = productRef.url;
        const { data } = await publicClient.get(`${apiUrl}/api/public/product-config`, {
          headers: { 'X-Widget-Key': widgetKey },
          params,
        });
        if (data.assistant_ready === false) {
          // The merchant has not added an OpenAI key yet: nothing could be
          // answered, so show nothing at all.
          console.warn('[product-assistant] widget hidden: the store has no OpenAI API key configured.');
          setResolved(false);
          window.parent.postMessage({ action: 'product-chat-hide' }, '*');
          return;
        }
        if (!data.resolved) {
          // Valid tenant but this product isn't indexed — never show a broken
          // box to shoppers; disappear and leave a hint for the merchant.
          console.warn('[product-assistant] product not found in the indexed catalog; widget hidden.');
          setResolved(false);
          window.parent.postMessage({ action: 'product-chat-hide' }, '*');
          return;
        }
        setResolved(true);
        setQuickQuestions((data.quick_questions || []).slice(0, 3));
        setPrimaryColor(data.primary_color || '');
        setHeaderTitle(data.header_title || '');
        setCustomCss(data.custom_css || '');
        setPoweredByText(data.powered_by_text ?? '');
      } catch (err) {
        // Disabled tenant / invalid key / backend down: hide the widget
        // completely rather than rendering any placeholder on the page.
        console.warn(
          '[product-assistant] widget hidden:',
          err?.response?.status === 403
            ? `the backend refused the config (${err?.response?.data?.detail || 'assistant not enabled for this tenant'}).`
            : 'the product assistant is unavailable.',
        );
        setResolved(false);
        window.parent.postMessage({ action: 'product-chat-hide' }, '*');
      }
    })();
  }, [ready, apiUrl, widgetKey, productRef]);

  // Tell the parent frame how tall we are so the inline iframe can grow/shrink.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        window.parent.postMessage({ action: 'product-chat-resize', height: entry.contentRect.height + 20 }, '*');
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [resolved, turns, quickQuestions, chatOpen]);

  const handleAsk = useCallback(
    async (text, { fromChip = false } = {}) => {
      const question = String(text || '').trim();
      if (!question || isLoading) return;
      setChatOpen(true);
      if (fromChip) setAskedQuestions((prev) => [...prev, question]);
      setInput('');
      setIsLoading(true);
      setTurns((prev) => [...prev, { question, answer: null }]);
      const send = () =>
        publicClient.post(
          `${apiUrl}/api/public/product-chat`,
          {
            message: question,
            session_id: sessionId,
            product_ref: { entity_id: productRef.entityId, sku: productRef.sku, url: productRef.url },
          },
          { headers: { 'X-Widget-Key': widgetKey, 'X-Visitor-Id': visitorId } },
        );
      // One silent retry for transient failures (network blip, timeout, 5xx).
      // 4xx answers are deterministic, so retrying them would only add delay.
      const isTransient = (err) => !err?.response || err.response.status >= 500;
      try {
        let result;
        try {
          result = await send();
        } catch (firstErr) {
          if (!isTransient(firstErr)) throw firstErr;
          await new Promise((r) => setTimeout(r, 1200));
          result = await send();
        }
        const { data } = result;
        if (data.session_id) {
          setSessionId(data.session_id);
          if (sessionStorageKey) localStorage.setItem(sessionStorageKey, data.session_id);
        }
        setTurns((prev) => {
          const next = [...prev];
          next[next.length - 1] = { question, answer: data.response };
          return next;
        });
      } catch (err) {
        const status = err?.response?.status;
        const timedOut = err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''));
        let answer;
        if (status === 429) {
          answer = 'This assistant has reached its usage limit for now. Please try again in a few minutes.';
        } else if (status === 503) {
          answer = 'The assistant is temporarily unavailable for this store. Please check back shortly.';
        } else if (status === 403) {
          answer = 'The assistant is not available on this page.';
        } else if (timedOut || !err?.response) {
          answer = 'The assistant is taking longer than usual to respond. Please try again.';
        } else {
          answer = "I couldn't get an answer to that just now. Please try again, or ask about the price, stock or specifications.";
        }
        console.warn('[product-assistant] product-chat failed:', status || err?.code || err?.message, err?.response?.data?.detail || '');
        setTurns((prev) => {
          const next = [...prev];
          next[next.length - 1] = { question, answer, isError: true, retryable: status !== 403 };
          return next;
        });
      } finally {
        setIsLoading(false);
      }
    },
    [apiUrl, widgetKey, visitorId, sessionId, productRef, sessionStorageKey, isLoading],
  );

  const openAskAnything = () => {
    setChatOpen(true);
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleAsk(input);
  };

  const remainingQuestions = quickQuestions.filter((q) => !askedQuestions.includes(q));

  const cssVars = useMemo(() => {
    const accent = primaryColor || '#1a56db';
    return {
      '--pc-accent': accent,
      '--pc-chip-color': accent,
      // Light tint of the accent for pill backgrounds (GC-style std-blue-100 look).
      '--pc-chip-bg': `color-mix(in srgb, ${accent} 11%, white)`,
    };
  }, [primaryColor]);

  if (!ready) return null;

  // Disabled tenant / bad key / unindexed product: render nothing at all —
  // the parent loader has already been told to remove the iframe.
  if (resolved !== true) return null;

  const questionChips = remainingQuestions.map((q) => (
    <QuestionChip key={q} type="button" onClick={() => handleAsk(q, { fromChip: true })} disabled={isLoading}>
      {q}
    </QuestionChip>
  ));

  const panelTitle = headerTitle || 'AI Product Assistant';

  return (
    <Panel ref={containerRef} style={cssVars}>
      {/* Tenant-authored CSS (Admin → Product Assistant → Custom CSS) —
          targets the stable pa-* class names, e.g. .pa-chip, .pa-heading. */}
      {customCss ? <style>{customCss}</style> : null}
      <PanelHeading>
        <Sparkle />
        <span>{panelTitle}</span>
      </PanelHeading>
      {!chatOpen ? (
        // Initial state: suggestion pills only — no input, no chat chrome.
        <ChipRow>
          {questionChips}
          <AskAnythingChip type="button" onClick={openAskAnything} disabled={isLoading}>
            <span>Ask anything</span>
            <Sparkle />
          </AskAnythingChip>
        </ChipRow>
      ) : (
        <>
          {(turns.length > 0 || remainingQuestions.length > 0) && (
            <ConversationCard ref={conversationRef}>
              {turns.map((turn, i) => (
                <Turn key={i}>
                  <QuestionPillRow>
                    <QuestionPill>{turn.question}</QuestionPill>
                  </QuestionPillRow>
                  {turn.answer == null ? <Thinking>Thinking…</Thinking> : <AnswerText>{renderAnswerText(turn.answer)}</AnswerText>}
                  {turn.isError && turn.retryable && i === turns.length - 1 && !isLoading && (
                    <RetryButton
                      onClick={() => {
                        setTurns((prev) => prev.slice(0, -1));
                        handleAsk(turn.question);
                      }}
                    >
                      Try again
                    </RetryButton>
                  )}
                </Turn>
              ))}
              {/* No "Ask anything" chip here — the input below already covers it. */}
              {remainingQuestions.length > 0 && <SuggestionsInCard>{questionChips}</SuggestionsInCard>}
            </ConversationCard>
          )}
          <InputRow onSubmit={handleSubmit}>
            <InputField
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything"
              disabled={isLoading}
            />
            <SendButton type="submit" disabled={isLoading || !input.trim()} aria-label="Send">
              <SendArrow />
            </SendButton>
          </InputRow>
          {poweredByText ? <PoweredBy>{poweredByText}</PoweredBy> : null}
        </>
      )}
    </Panel>
  );
};

export default ProductChatWidget;
